import { supabase } from '@cleanpro/common-ui';
import { pauseThreshold } from '@cleanpro/common-ui';
import type {
  GrowthProvider,
  GrowthProviderRow,
  OptimizerAction,
  OptimizerActionRow,
  GrowthMetrics,
  AdNetwork,
  NetworkCampaignRow,
  NetworkCpaqRow,
  AllocatorDecision,
  PerNetworkReport,
  ObservabilityMetrics,
} from './types';

// ─── Constants ─────────────────────────────────────────────────

/** Fallback daily budget (USD) used when admin enables ads on behalf of a provider with no existing campaign. */
const DEFAULT_DAILY_BUDGET = 50; // USD

function getFunctionHeaders(authToken: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authToken}`,
  };

  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (supabaseAnonKey) {
    headers.apikey = supabaseAnonKey;
  }

  return headers;
}

// ─── Transformers ──────────────────────────────────────────────

const transformProvider = (
  row: GrowthProviderRow,
  stats: {
    activeCampaigns: number;
    leadsThisWeek: number;
    weeklySpend: number;
    dailyBurn: number;
    pauseThreshold: number;
  }
): GrowthProvider => ({
  id: row.id,
  businessName: row.business_name,
  ownerName: row.owner_name,
  email: row.email,
  subscriptionPlan: row.subscription_plan,
  walletBalance: Number(row.wallet_balance) || 0,
  growthAdsEnabled: row.growth_ads_enabled,
  growthAdsPaused: row.growth_ads_paused,
  maxLeadsPerWeek: row.max_leads_per_week,
  ...stats,
});

const transformOptimizerAction = (row: OptimizerActionRow): OptimizerAction => ({
  id: row.id,
  businessId: row.business_id,
  businessName: row.businesses?.business_name ?? 'Unknown',
  campaignId: row.campaign_id,
  actionType: row.action_type,
  details: row.details,
  createdAt: row.created_at,
});

// ─── Growth Providers ──────────────────────────────────────────

const GROWTH_PLANS = ['growth', 'Growth'];

export async function fetchGrowthProviders(
  isPlatformAdmin = false
): Promise<GrowthProvider[]> {
  let query = supabase
    .from('businesses')
    .select(
      'id, business_name, owner_name, email, subscription_plan, wallet_balance, growth_ads_enabled, growth_ads_paused, max_leads_per_week'
    )
    .order('business_name', { ascending: true });

  // Platform admins see all orgs; others only see growth-plan orgs
  if (!isPlatformAdmin) {
    query = query.in('subscription_plan', GROWTH_PLANS);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching growth providers:', error);
    throw error;
  }

  if (!data || data.length === 0) return [];

  // Fetch campaign & lead stats per provider
  const providerIds = data.map((r) => r.id);

  const { data: campaignRows } = await supabase
    .from('ad_campaigns')
    .select('business_id, status, daily_budget')
    .in('business_id', providerIds);

  // Get weekly lead counts from wallet_transactions (debits this week)
  const startOfWeek = getISOWeekStart();
  const { data: weeklyTxns } = await supabase
    .from('wallet_transactions')
    .select('business_id, amount, type')
    .in('business_id', providerIds)
    .eq('type', 'debit')
    .gte('created_at', startOfWeek);

  return data.map((row) => {
    const campaigns = campaignRows?.filter((c) => c.business_id === row.id) ?? [];
    const txns = weeklyTxns?.filter((t) => t.business_id === row.id) ?? [];
    const activeCampaigns = campaigns.filter((c) => c.status === 'active');
    const totalDailyBudget = activeCampaigns.reduce(
      (sum, c) => sum + (Number(c.daily_budget) || 0),
      0
    );
    const dailyBurn = totalDailyBudget * (10 / 9);
    return transformProvider(row as GrowthProviderRow, {
      activeCampaigns: activeCampaigns.length,
      leadsThisWeek: txns.length,
      weeklySpend: txns.reduce((sum, t) => sum + Math.abs(Number(t.amount)), 0),
      dailyBurn,
      pauseThreshold: pauseThreshold(totalDailyBudget),
    });
  });
}

// ─── Optimizer Audit Log ───────────────────────────────────────

export async function fetchOptimizerActions(
  limit = 100,
  businessId?: string
): Promise<OptimizerAction[]> {
  let query = supabase
    .from('optimizer_actions')
    .select(
      'id, business_id, campaign_id, action_type, details, created_at, businesses(business_name)'
    )
    .order('created_at', { ascending: false })
    .limit(limit);

  if (businessId) {
    query = query.eq('business_id', businessId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching optimizer actions:', error);
    throw error;
  }

  return (data ?? []).map((row) =>
    transformOptimizerAction(row as unknown as OptimizerActionRow)
  );
}

// ─── Growth Metrics ────────────────────────────────────────────

export async function fetchGrowthMetrics(): Promise<GrowthMetrics> {
  const [providersResult, lifetimeResult, weeklyTxnsResult] = await Promise.all([
    supabase
      .from('businesses')
      .select('id, subscription_plan, wallet_balance, growth_ads_enabled, growth_ads_paused')
      .in('subscription_plan', ['growth', 'Growth', 'steady', 'Steady']),
    supabase
      .from('ad_campaigns')
      .select('lifetime_spend, lifetime_platform_fee'),
    supabase
      .from('wallet_transactions')
      .select('amount, type')
      .eq('type', 'debit')
      .gte('created_at', getISOWeekStart()),
  ]);

  const providers = providersResult.data ?? [];
  const activeProviders = providers.filter(
    (p) => p.growth_ads_enabled && !p.growth_ads_paused
  );
  const pausedProviders = providers.filter(
    (p) => p.growth_ads_enabled && p.growth_ads_paused
  );

  const totalWalletBalance = providers.reduce(
    (sum, p) => sum + (Number(p.wallet_balance) || 0),
    0
  );

  const lifetimeRows = lifetimeResult.data ?? [];
  const totalLifetimeAdSpend = lifetimeRows.reduce(
    (sum, r) => sum + (Number(r.lifetime_spend) || 0),
    0
  );
  const totalLifetimePlatformFee = lifetimeRows.reduce(
    (sum, r) => sum + (Number(r.lifetime_platform_fee) || 0),
    0
  );

  const weeklyTxns = weeklyTxnsResult.data ?? [];

  return {
    totalProviders: providers.length,
    activeProviders: activeProviders.length,
    pausedProviders: pausedProviders.length,
    totalWalletBalance,
    totalLifetimeAdSpend,
    totalLifetimePlatformFee,
    leadsThisWeek: weeklyTxns.length,
    weeklyRevenue: weeklyTxns.reduce(
      (sum, t) => sum + Math.abs(Number(t.amount)),
      0
    ),
  };
}

// ─── Force Controls ────────────────────────────────────────────

/**
 * Provision a new Meta campaign for a Growth tier provider
 */
async function provisionMetaCampaign(
  businessId: string,
  dailyBudget: number,
  serviceId: string,
  authToken: string,
  leadCap?: number
): Promise<void> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const functionUrl = `${supabaseUrl}/functions/v1/provision-meta-campaign`;

  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: getFunctionHeaders(authToken),
    body: JSON.stringify({
      businessId,
      dailyBudget,
      serviceId,
      leadCap,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    
    // Provide admin-specific error messages for token issues
    if (error.error === 'TOKEN_ERROR' || error.message?.includes('access token')) {
      throw new Error(
        'Meta campaign provisioning failed due to expired/invalid access token.\n\n' +
        '🔧 ADMIN ACTION REQUIRED:\n' +
        '1. Generate a System User Token (never expires)\n' +
        '2. Update Supabase secret: META_ACCESS_TOKEN\n\n' +
        'See: /docs/META_SYSTEM_USER_TOKEN_SETUP.md for complete instructions.\n\n' +
        'Original error: ' + (error.message || 'Unknown token error')
      );
    }
    
    throw new Error(error.message || 'Failed to provision campaign');
  }
}

export async function forceToggleGrowthAds(
  businessId: string,
  enabled: boolean,
  authToken: string
): Promise<void> {
  // If enabling ads, first check if provider has any existing campaigns
  if (enabled) {
    const { count } = await supabase
      .from('ad_campaigns')
      .select('id', { count: 'exact', head: true })
      .eq('business_id', businessId)
      .neq('status', 'archived');

    // Only provision if they don't have any active/paused campaigns
    if (count === 0) {
      const [{ data: business }, { data: serviceRows, error: servicesError }] = await Promise.all([
        supabase
          .from('businesses')
          .select('max_leads_per_week')
          .eq('id', businessId)
          .single(),
        supabase
          .from('business_services')
          .select('service_id')
          .eq('business_id', businessId)
          .eq('is_active', true)
          .limit(1),
      ]);

      if (servicesError) {
        console.error('Error fetching business services:', servicesError);
        throw servicesError;
      }

      const serviceId = serviceRows?.[0]?.service_id;
      if (!serviceId) {
        throw new Error('Provider must have at least one active service before enabling Growth ads');
      }

      const dailyLeadCap = business?.max_leads_per_week
        ? Math.ceil(business.max_leads_per_week / 7)
        : undefined;

      // Provision the campaign before enabling
      await provisionMetaCampaign(
        businessId,
        DEFAULT_DAILY_BUDGET,
        serviceId,
        authToken,
        dailyLeadCap
      );
    }
  }

  // Now update the database flag
  const { error } = await supabase
    .from('businesses')
    .update({ growth_ads_enabled: enabled })
    .eq('id', businessId);

  if (error) {
    console.error('Error toggling growth ads:', error);
    throw error;
  }
}

export async function forceTogglePause(
  businessId: string,
  paused: boolean
): Promise<void> {
  const { error } = await supabase
    .from('businesses')
    .update({ growth_ads_paused: paused })
    .eq('id', businessId);

  if (error) {
    console.error('Error toggling pause:', error);
    throw error;
  }
}

// ─── Helpers ───────────────────────────────────────────────────

function getISOWeekStart(): string {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(now.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return monday.toISOString();
}

// ─── Per-Network Report (Issue #440) ───────────────────────────

/**
 * Fetch per-network report for a single provider.
 * Returns campaigns split by network, CPAQ per network, and allocator decision log.
 */
export async function fetchPerNetworkReport(businessId: string): Promise<PerNetworkReport> {
  const [bizResult, campaignsResult, strategyResult] = await Promise.all([
    supabase
      .from('businesses')
      .select('id, business_name')
      .eq('id', businessId)
      .single(),
    // Cast to unknown since ad_campaigns gains network column in Wave 1 schema migration
    (supabase as unknown as {
      from: (t: string) => {
        select: (s: string) => {
          eq: (c: string, v: string) => {
            order: (c: string, o: object) => Promise<{ data: Array<Record<string, unknown>> | null; error: unknown }>;
          };
        };
      };
    })
      .from('ad_campaigns')
      .select('id, name, network, status, daily_budget, lifetime_spend, lifetime_platform_fee, services(name)')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false }),
    (supabase as unknown as {
      from: (t: string) => {
        select: (s: string) => {
          eq: (c: string, v: string) => {
            maybeSingle: () => Promise<{ data: Record<string, unknown> | null; error: unknown }>;
          };
        };
      };
    })
      .from('growth_strategies')
      .select('allocation_meta_pct, allocation_google_pct, allocation_lsa_pct, mode')
      .eq('business_id', businessId)
      .maybeSingle(),
  ]);

  const biz = bizResult.data;
  if (!biz) throw new Error('Business not found');

  const rawCampaigns = (campaignsResult.data ?? []) as Array<{
    id: string;
    name: string;
    network: string | null;
    status: string;
    daily_budget: number | string | null;
    lifetime_spend: number | string | null;
    services: { name: string } | null;
  }>;

  // Aggregate performance per campaign
  const campaignIds = rawCampaigns.map((c) => c.id);
  const { data: perfRows } = await supabase
    .from('campaign_performance')
    .select('campaign_id, spend, leads')
    .in('campaign_id', campaignIds.length > 0 ? campaignIds : ['__none__']);

  const perfMap = new Map<string, { totalSpend: number; totalLeads: number }>();
  for (const row of perfRows ?? []) {
    const existing = perfMap.get(row.campaign_id) ?? { totalSpend: 0, totalLeads: 0 };
    existing.totalSpend += Number(row.spend) || 0;
    existing.totalLeads += Number(row.leads) || 0;
    perfMap.set(row.campaign_id, existing);
  }

  const campaigns: NetworkCampaignRow[] = rawCampaigns.map((c) => {
    const perf = perfMap.get(c.id) ?? { totalSpend: 0, totalLeads: 0 };
    return {
      id: c.id,
      name: c.name,
      network: (c.network as AdNetwork) ?? 'meta',
      status: (c.status as NetworkCampaignRow['status']) ?? 'draft',
      dailyBudget: Number(c.daily_budget) || 0,
      totalSpend: perf.totalSpend,
      totalLeads: perf.totalLeads,
    };
  });

  // CPAQ per network (trailing 7d and 30d)
  const now = new Date();
  const d7ago = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const d30ago = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const attrBase = (supabase as any)
    .from('lead_attribution')
    .select('network, attributed_quote_value, created_at')
    .eq('business_id', businessId)
    .eq('status', 'accepted');

  const [attr7Result, attr30Result] = await Promise.all([
    attrBase.gte('created_at', d7ago),
    attrBase.gte('created_at', d30ago),
  ]);

  const networks: AdNetwork[] = ['meta', 'google_ads', 'google_lsa'];
  const cpaq: NetworkCpaqRow[] = networks.map((network) => {
    const rows7 = ((attr7Result.data ?? []) as Array<{ network: string; attributed_quote_value: number | null }>)
      .filter((r) => r.network === network);
    const rows30 = ((attr30Result.data ?? []) as Array<{ network: string; attributed_quote_value: number | null }>)
      .filter((r) => r.network === network);

    // CPAQ = total cost / accepted quotes
    const cost7 = campaigns
      .filter((c) => c.network === network)
      .reduce((s, c) => s + c.totalSpend, 0); // rough; use perf data for precision
    const cost30 = cost7; // same campaigns — use as proxy

    return {
      network,
      cpaqTrailing7d: rows7.length > 0 ? Math.round((cost7 / rows7.length) * 100) / 100 : null,
      cpaqTrailing30d: rows30.length > 0 ? Math.round((cost30 / rows30.length) * 100) / 100 : null,
    };
  });

  // Allocator decision log from growth_strategies history
  // growth_strategy_history is a new table added in the Wave 1 schema; cast to unknown.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const historyResult = await (supabase as any)
    .from('growth_strategy_history')
    .select('id, decided_at, allocation_meta_pct, allocation_google_pct, allocation_lsa_pct, last_rebalance_reason')
    .eq('business_id', businessId)
    .order('decided_at', { ascending: false })
    .limit(60);

  const allocatorDecisions: AllocatorDecision[] = ((historyResult.data ?? []) as Array<{
    id: string;
    decided_at: string;
    allocation_meta_pct: number;
    allocation_google_pct: number;
    allocation_lsa_pct: number;
    last_rebalance_reason: string | null;
  }>).map((row) => ({
    id: row.id,
    decidedAt: row.decided_at,
    metaPct: Number(row.allocation_meta_pct),
    googlePct: Number(row.allocation_google_pct),
    lsaPct: Number(row.allocation_lsa_pct),
    rebalanceReason: row.last_rebalance_reason ?? '',
  }));

  const rawStrategy = strategyResult.data as {
    allocation_meta_pct: number;
    allocation_google_pct: number;
    allocation_lsa_pct: number;
    mode: 'rules' | 'ml';
  } | null;

  return {
    businessId,
    businessName: (biz as { business_name: string }).business_name,
    campaigns,
    cpaq,
    allocatorDecisions,
    currentStrategy: rawStrategy
      ? {
          metaPct: Number(rawStrategy.allocation_meta_pct),
          googlePct: Number(rawStrategy.allocation_google_pct),
          lsaPct: Number(rawStrategy.allocation_lsa_pct),
          mode: rawStrategy.mode,
        }
      : null,
  };
}

/**
 * Apply a manual allocation override for a provider.
 * Writes directly to growth_strategies and records the audit reason.
 */
export async function applyAllocationOverride(
  businessId: string,
  allocation: { metaPct: number; googlePct: number; lsaPct: number },
  adminClerkUserId: string
): Promise<void> {
  const reason = `manual override by admin ${adminClerkUserId}`;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any)
    .from('growth_strategies')
    .update({
      allocation_meta_pct: allocation.metaPct,
      allocation_google_pct: allocation.googlePct,
      allocation_lsa_pct: allocation.lsaPct,
      last_rebalance_reason: reason,
      updated_at: new Date().toISOString(),
    })
    .eq('business_id', businessId);

  if (error) throw new Error('Failed to apply allocation override');
}

/**
 * Pause or resume a specific network for a provider by pausing/resuming all
 * campaigns on that network.
 */
export async function setNetworkPaused(
  businessId: string,
  network: AdNetwork,
  paused: boolean,
  authToken: string
): Promise<void> {
  const functionName = paused ? 'pause-campaign' : 'resume-campaign';
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  // Fetch campaign IDs for this business + network
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: campaigns } = await (supabase as any)
    .from('ad_campaigns')
    .select('id')
    .eq('business_id', businessId)
    .eq('network', network)
    .neq('status', 'archived');

  for (const campaign of (campaigns ?? []) as Array<{ id: string }>) {
    const response = await fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
      method: 'POST',
      headers: getFunctionHeaders(authToken),
      body: JSON.stringify({ campaignId: campaign.id }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(`Failed to ${functionName} campaign ${campaign.id}: ${err.message}`);
    }
  }
}

// ─── Observability Metrics (Issue #441) ────────────────────────

/**
 * Aggregate platform-wide observability metrics for the admin dashboard.
 * Queries last 30 days of campaign_performance, lead_attribution,
 * growth_strategy_history, and optimizer_actions (error type).
 */
export async function fetchObservabilityMetrics(): Promise<ObservabilityMetrics> {
  const now = new Date();
  const d30ago = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const d7ago = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const db = supabase as any;

  const [perfResult, attrResult, stratHistResult, errActionsResult, lsaAttrResult, lsaTxnResult] =
    await Promise.all([
      // Daily spend per campaign (joined to network)
      db
        .from('campaign_performance')
        .select('date, spend, ad_campaigns!inner(network)')
        .gte('date', d30ago)
        .order('date', { ascending: true }),
      // CPAQ trailing 7d per network
      db
        .from('lead_attribution')
        .select('network, attributed_quote_value')
        .gte('created_at', d7ago)
        .eq('status', 'accepted'),
      // Allocator decisions per day
      db
        .from('growth_strategy_history')
        .select('decided_at')
        .gte('decided_at', d30ago)
        .order('decided_at', { ascending: true }),
      // Google Ads API errors from optimizer_actions
      supabase
        .from('optimizer_actions')
        .select('id', { count: 'exact', head: true })
        .eq('action_type', 'error')
        .gte('created_at', d7ago),
      // LSA lead_attribution rows (last 7d)
      db
        .from('lead_attribution')
        .select('id', { count: 'exact', head: true })
        .eq('network', 'google_lsa')
        .gte('created_at', d7ago),
      // LSA wallet transaction charges (last 7d)
      supabase
        .from('wallet_transactions')
        .select('id', { count: 'exact', head: true })
        .eq('source', 'lsa_lead_charge')
        .gte('created_at', d7ago),
    ]);

  // Daily spend grouped by date + network
  const spendMap = new Map<string, { meta: number; googleAds: number; googleLsa: number }>();
  for (const row of (perfResult.data ?? []) as Array<{
    date: string;
    spend: number;
    ad_campaigns: { network: string };
  }>) {
    const entry = spendMap.get(row.date) ?? { meta: 0, googleAds: 0, googleLsa: 0 };
    const network = row.ad_campaigns?.network;
    if (network === 'meta') entry.meta += Number(row.spend) || 0;
    else if (network === 'google_ads') entry.googleAds += Number(row.spend) || 0;
    else if (network === 'google_lsa') entry.googleLsa += Number(row.spend) || 0;
    spendMap.set(row.date, entry);
  }
  const dailySpendByNetwork = Array.from(spendMap.entries()).map(([date, v]) => ({ date, ...v }));

  // CPAQ trailing 7d per network
  const networks: AdNetwork[] = ['meta', 'google_ads', 'google_lsa'];
  const attrRows = (attrResult.data ?? []) as Array<{ network: string; attributed_quote_value: number | null }>;
  const cpaqByNetwork = networks.map((network) => {
    const rows = attrRows.filter((r) => r.network === network);
    if (rows.length === 0) return { network, cpaqTrailing7d: null };
    const totalValue = rows.reduce((s, r) => s + (Number(r.attributed_quote_value) || 0), 0);
    return { network, cpaqTrailing7d: rows.length > 0 ? Math.round((totalValue / rows.length) * 100) / 100 : null };
  });

  // Allocator decisions per day
  const decisionsMap = new Map<string, number>();
  for (const row of (stratHistResult.data ?? []) as Array<{ decided_at: string }>) {
    const date = row.decided_at.slice(0, 10);
    decisionsMap.set(date, (decisionsMap.get(date) ?? 0) + 1);
  }
  const allocatorDecisionsPerDay = Array.from(decisionsMap.entries()).map(([date, count]) => ({ date, count }));

  const googleAdsErrorCount = errActionsResult.count ?? 0;
  const leadAttributionCount = lsaAttrResult.count ?? 0;
  const walletChargeCount = lsaTxnResult.count ?? 0;

  return {
    dailySpendByNetwork,
    cpaqByNetwork,
    allocatorDecisionsPerDay,
    googleAdsErrorCount,
    lsaChargeDrift: {
      leadAttributionCount,
      walletChargeCount,
      drift: leadAttributionCount - walletChargeCount,
    },
  };
}
